import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Calendar, Phone, Trophy, Users } from 'lucide-react';
import { FaqSection } from '../components/SEO';
import { FeaturedEventCard } from '../components/FeaturedEventCard';
import { ORGANIZATION_LD } from '../components/SEO';
import { PageHero } from '../components/PageShell';
import { PageShell } from '../components/PageShell';
import { SEO } from '../components/SEO';
import { SITE_URL } from '../lib/constants';
import { WhatsAppButton, WhatsAppIcon } from '../components/WhatsAppButton';
import { buildFaqLd } from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';

const EventsPage = () => {
  const { t } = useLanguage();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const seo = t.seo.events;
  const faqs = t.eventsPage.faqs || [];
  const heroImg = 'https://customer-assets.emergentagent.com/job_padel-arena-modern/artifacts/vqamn5j5_DSC07851.jpg';
  const types = [
    { titleKey: 'corporate', icon: <Building2 className="w-7 h-7" /> },
    { titleKey: 'birthday', icon: <Users className="w-7 h-7" /> },
    { titleKey: 'tournament', icon: <Trophy className="w-7 h-7" /> }
  ];
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${SITE_URL}/events#service`,
      name: 'Private events, corporate events & tournaments at Padelarena Wädenswil',
      url: `${SITE_URL}/events`,
      image: heroImg,
      description: seo.description,
      provider: { '@id': `${SITE_URL}/#organization` },
      areaServed: ORGANIZATION_LD.areaServed,
      serviceType: ['Corporate event', 'Birthday party', 'Tournament organization', 'Team building'],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Event packages',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: t.eventsPage.corporateTitle, description: t.eventsPage.corporateDesc } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: t.eventsPage.birthdayTitle, description: t.eventsPage.birthdayDesc } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: t.eventsPage.tournamentTitle, description: t.eventsPage.tournamentDesc } }
        ]
      }
    },
    faqs.length ? buildFaqLd(faqs) : null
  ].filter(Boolean);

  return (
    <PageShell testId="events-page">
      <SEO path="/events" title={seo.title} description={seo.description} keywords={seo.keywords} image={heroImg} jsonLd={jsonLd} />
      <PageHero
        label={t.eventsPage.label}
        title={t.eventsPage.titleStart}
        accent={t.eventsPage.titleAccent}
        subtitle={t.eventsPage.subtitle}
        image={heroImg}
      />
      <section className="py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-6 md:px-12 space-y-14">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">{t.eventsPage.upcomingTitle}</h2>
            <FeaturedEventCard eventKey="juniorCamp" testId="featured-event-juniorcamp-events" />
          </div>
          <div>
            <div
              className="relative overflow-hidden rounded-3xl border-2 border-dashed border-slate-300 bg-gradient-to-br from-slate-50 via-white to-sky-50 p-8 md:p-12 text-center"
              data-testid="events-paused-card"
            >
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-xs font-bold uppercase tracking-widest mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                {t.eventsPage.pausedBadge}
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3 leading-tight">
                {t.eventsPage.pausedTitle}
              </h3>
              <p className="text-slate-700 max-w-2xl mx-auto mb-3 leading-relaxed">
                {t.eventsPage.pausedBody}
              </p>
              <p className="text-slate-500 text-sm max-w-2xl mx-auto mb-6">
                {t.eventsPage.pausedNote}
              </p>
              <div className="mx-auto max-w-2xl bg-gradient-to-br from-[#5B7FA8]/10 to-sky-100/50 border border-[#5B7FA8]/20 rounded-2xl px-5 py-4 mb-6">
                <p className="text-slate-800 font-semibold text-[0.95rem] mb-4" data-testid="events-signup-hook">
                  {t.eventsPage.signupHook}
                </p>
                <a
                  href="https://bookpadelarena.ch/community"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="events-signup-cta"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#5B7FA8] hover:bg-[#4A6D95] text-white font-bold transition-colors shadow-lg shadow-[#5B7FA8]/30"
                >
                  <Calendar className="w-5 h-5" />
                  {t.eventsPage.signupCta}
                </a>
              </div>
              <WhatsAppButton
                linkKey="community"
                testId="events-paused-community-btn"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 font-semibold text-sm transition-colors"
              >
                <WhatsAppIcon className="w-4 h-4 text-[#5B7FA8]" />
                {t.eventsPage.pausedCta}
              </WhatsAppButton>
            </div>
          </div>
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">{t.eventsPage.privateTitle}</h2>
            <p className="text-slate-700 leading-relaxed mb-6">{t.eventsPage.privateIntro}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {types.map((tp) => (
                <div key={tp.titleKey} className="bg-white border border-slate-200 rounded-2xl p-6">
                  <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center mb-4">
                    {tp.icon}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{t.eventsPage[`${tp.titleKey}Title`]}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{t.eventsPage[`${tp.titleKey}Desc`]}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-blue-600 text-white rounded-3xl p-8 md:p-10 text-center">
            <h3 className="text-2xl md:text-3xl font-bold mb-3">{t.eventsPage.ctaTitle}</h3>
            <p className="text-blue-100 mb-6">{t.eventsPage.ctaSubtitle}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/#contact" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold bg-white text-blue-600 hover:bg-blue-50 transition-colors">
                {t.eventsPage.requestQuote}
              </Link>
              <a href="tel:+41438330834" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold bg-blue-700 text-white hover:bg-blue-800 transition-colors">
                <Phone className="w-4 h-4" /> +41 43 833 0834
              </a>
            </div>
          </div>

          {faqs.length > 0 && (
            <FaqSection title={t.eventsPage.faqTitle} faqs={faqs} />
          )}
        </div>
      </section>
    </PageShell>
  );
};

// Sports Bar Page

export default EventsPage;
